import { createContext, useContext, useMemo } from "react";
import type { MutableRefObject, PropsWithChildren } from "react";
import { useEffect } from "react";
import { noop } from "lodash";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useFetchCheckoutData } from "@/react/hooks/paywalls/useFetchCheckoutData";
import { useGetPaywallsCurrencies } from "@/react/hooks/paywalls/useGetPaywallsCurrencies";
import type { Paywall, PaywallCurrencyCheckout } from "@/react/types";

export interface PaywallFormProviderProp {
  checkoutDataQuery?: ReturnType<typeof useFetchCheckoutData>["showQuery"];
  onClose: (withConfirmation?: boolean) => void;
  selectedCurrency?: PaywallCurrencyCheckout;
  supportedCurrenciesQuery?: ReturnType<typeof useGetPaywallsCurrencies>;
  validToClose?: MutableRefObject<() => boolean>;
}

const PaywallFormProvider = createContext<PaywallFormProviderProp>({
  onClose: noop,
});
PaywallFormProvider.displayName = "PaywallFormProvider";

export const usePaywallFormContext = () => useContext(PaywallFormProvider);

export const PaywallFormProviderContext = ({
  children,
  validToClose,
  onClose,
}: PropsWithChildren<PaywallFormProviderProp>) => {
  const supportedCurrenciesQuery = useGetPaywallsCurrencies();
  const {
    getValues,
    formState: { isDirty },
  } = useFormContext<Paywall>();
  const paywall = getValues();

  const selectedCurrency = useMemo<PaywallCurrencyCheckout | undefined>(
    () =>
      supportedCurrenciesQuery?.data?.find(
        currency => currency.id === Number(paywall.currency_id),
      ),
    [paywall?.currency_id, supportedCurrenciesQuery?.data],
  );

  const { showQuery: checkoutDataQuery } = useFetchCheckoutData(
    {
      preview_paywall: true,
      currency_code: selectedCurrency?.code,
    },
    !!selectedCurrency?.code,
  );

  useEffect(() => {
    if (validToClose) {
      validToClose.current = () => {
        if (!isDirty) return true;
        return (
          isDirty && confirm(t("settings.paywalls.modal.tabs.close_warning"))
        );
      };
    }
  }, [isDirty, validToClose]);

  const value = {
    supportedCurrenciesQuery,
    selectedCurrency,
    checkoutDataQuery,
    onClose,
  };

  return (
    <PaywallFormProvider.Provider value={value}>
      {children}
    </PaywallFormProvider.Provider>
  );
};
